import { apiClient } from "@base/api/client";
import type { PaginationResponse } from "@base/model/types";
import { type InfiniteData, type UseMutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Comment } from "./schema";

type CommentsPageResponse = PaginationResponse<Comment[]>;
type CommentsInfiniteData = InfiniteData<CommentsPageResponse>;

async function deleteComment(projectId: string, logId: string, commentId: number): Promise<void> {
  await apiClient.delete(`/project-management/api/projects/${projectId}/daily-logs/${logId}/comments/${commentId}`);
}

function removeCommentFromPages(
  old: CommentsInfiniteData | undefined,
  commentId: number,
): CommentsInfiniteData | undefined {
  if (!old) return old;

  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: page.results.filter((c) => c.id !== commentId),
      count: page.results.some((c) => c.id === commentId) ? Math.max(0, page.count - 1) : page.count,
    })),
  };
}

export function useDeleteCommentMutation(
  projectId: string,
  logId: string,
  options?: Omit<UseMutationOptions<void, Error, number, unknown>, "mutationFn">,
) {
  const queryClient = useQueryClient();
  const commentsQueryKey = ["comments", projectId, logId] as const;

  return useMutation({
    ...options,

    mutationFn: (commentId: number) => deleteComment(projectId, logId, commentId),

    onMutate: async (commentId) => {
      await queryClient.cancelQueries({ queryKey: commentsQueryKey });

      const previousData = queryClient.getQueryData<CommentsInfiniteData>(commentsQueryKey);

      queryClient.setQueryData<CommentsInfiniteData>(commentsQueryKey, (old) => removeCommentFromPages(old, commentId));

      return { previousData };
    },

    onError: (error, commentId, context, mutation) => {
      if (context?.previousData) {
        queryClient.setQueryData<CommentsInfiniteData>(commentsQueryKey, context.previousData);
      }
      options?.onError?.(error, commentId, context, mutation);
    },

    onSettled: (data, error, commentId, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: commentsQueryKey });
      options?.onSettled?.(data, error, commentId, context, mutation);
    },
  });
}
